import { TRACK_USE_REQUESTED_OPTIONS } from 'utils/constants';
import { RESOLVER_ACTIONS, ROW_ACTION, RowAction } from './constants';

export type ResolverOption = { id: number | string; name: string };

/* One row of GET /clear/tracks-import/:jobId/unmatched — the BE ResolverRowDto
   (technical plan §06): items with the same vendor title AND vendor library collapse
   server-side into one row carrying every collapsed itemId (QW-158: title alone would
   merge same-titled tracks from different libraries onto a single resolution). Field
   names are the BE's (trackName/trackLibrary/usage); seedRow maps them onto the
   resolver's vendor-centric internal names. */
export type ApiResolverRow = {
  itemIds: string[];
  lineNumbers: string;
  trackLibrary: string;
  trackName: string;
  libraryTrackId?: string;
  usage: string;
  duration: string;
  status: 'unmatched' | 'matched';
  matchedTrack?: { id: number; title?: string; libraryId?: number; libraryName?: string };
};

export type RowResolution = {
  action: RowAction;
  library: ResolverOption | null;
  newLibraryName: string | null;
  track: ResolverOption | null;
  newTrackTitle: string | null;
  use: string | null;
  duration: string | null;
  duplicateOfLineNumbers: string | null;
  aliasRemoved: boolean;
};

export type ResolverRow = {
  /* First collapsed item id — the row's identity for reducer actions and React keys. */
  itemId: string;
  itemIds: string[];
  lineNumbers: string;
  vendorLibrary: string;
  vendorTitle: string;
  use: string;
  duration: string;
  /* True when the ENGINE matched this row (Pass A/B, or Pass 0 via a stored alias) —
     hidden from the grid (QW-129; a fully-matched import auto-finishes) and NOT the
     resolver's to touch: the BE rejects resolutions for MATCHED items with no
     resolutionAction (assertItemResolvable), and one such row in the payload 400s the
     whole FINISH IMPORT. Excluded from buildResolvePayload; still counted by
     deriveMatchedCount so the header shows the whole-file picture. To undo an alias
     match, delete the alias instead. */
  engineMatched: boolean;
  resolution: RowResolution;
};

export type StoredAlias = { id: number; vendorTitle: string; qwireTracksId: number; trackTitle?: string };

export type ResolverState = { rows: ResolverRow[]; divisionAliases: StoredAlias[]; loaded: boolean };

type ResolverAction =
  | {
      type: typeof RESOLVER_ACTIONS.ROWS_LOADED;
      apiRows: ApiResolverRow[];
      partners?: ResolverOption[];
      divisionAliases?: StoredAlias[];
    }
  | { type: typeof RESOLVER_ACTIONS.LIBRARY_SELECTED; itemId: string; library: ResolverOption }
  | { type: typeof RESOLVER_ACTIONS.LIBRARY_CREATED; itemId: string; name: string }
  | { type: typeof RESOLVER_ACTIONS.TRACK_SELECTED; itemId: string; track: ResolverOption }
  | { type: typeof RESOLVER_ACTIONS.TRACK_CREATED; itemId: string; title: string }
  | { type: typeof RESOLVER_ACTIONS.USE_CHANGED; itemId: string; use: string }
  | { type: typeof RESOLVER_ACTIONS.DURATION_CHANGED; itemId: string; duration: string | null }
  | { type: typeof RESOLVER_ACTIONS.ROW_CLEARED; itemId: string }
  | { type: typeof RESOLVER_ACTIONS.ALIAS_REMOVED; itemId: string }
  | { type: typeof RESOLVER_ACTIONS.DIVISION_ALIAS_DELETED; aliasId: number };

export const parseVendorUse = (use: string): string | null => {
  const trimmed = (use ?? '').trim();
  const isKnown = TRACK_USE_REQUESTED_OPTIONS.some((option: { id: string }) => option.id === trimmed);
  return isKnown ? trimmed : null;
};

const normalizePartnerName = (name: string): string => (name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const matchPartnerStrict = (vendorLibrary: string, partners: ResolverOption[]): ResolverOption | null => {
  const target = normalizePartnerName(vendorLibrary);
  if (!target) return null;

  const exact = partners.find((partner) => normalizePartnerName(partner.name) === target);
  if (exact) return { id: exact.id, name: exact.name };

  const contained = partners.filter((partner) => {
    const candidate = normalizePartnerName(partner.name);
    return candidate.includes(target) || target.includes(candidate);
  });
  if (contained.length === 1) return { id: contained[0].id, name: contained[0].name };

  return null;
};

/* Mirrors the BE's VALID_DURATION_PATTERN shapes (:ss / mm:ss / hh:mm:ss). A trailing
   AM/PM is stripped first: Excel time-formats duration cells, so pre-fix jobs can still
   deliver "01:35PM" meaning 1m35s. */
const DURATION_PATTERN = /^(?::([0-5]?\d)|([0-5]?\d):([0-5]?\d)|([0-2]?\d):([0-5]?\d):([0-5]?\d))$/;

const parseSingleDuration = (raw: string): string | null => {
  const cleaned = raw.trim().replace(/\s*[AP]\.?M\.?$/i, '');
  const match = cleaned.match(DURATION_PATTERN);
  if (!match) return null;
  const [, onlySeconds, min2, sec2, hours3, min3, sec3] = match;
  const hours = Number(hours3 ?? 0);
  const minutes = Number(min3 ?? min2 ?? 0);
  const seconds = Number(sec3 ?? sec2 ?? onlySeconds ?? 0);
  /* The drawer's Duration field displays mm:ss over a stored HH:mm:ss — an hours-bearing
     prefill would render truncated and silently import the unseen hours. Leave it empty
     and let the user read the raw value in the Imported Track Data column. */
  if (hours > 0) return null;
  return `00:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const parseVendorDuration = (duration: string): string | null => {
  /* Collapsed duplicate-title rows join their durations with commas; one Duration edit
     applies to every collapsed item, so prefill only when they all agree. */
  const parts = (duration ?? '').split(',').map(parseSingleDuration);
  if (parts.length === 0 || parts.some((part) => part == null)) return null;
  return parts.every((part) => part === parts[0]) ? parts[0] : null;
};

const firstLineNumber = (lineNumbers: string): number => {
  const first = parseInt(lineNumbers, 10);
  return Number.isNaN(first) ? Number.MAX_SAFE_INTEGER : first;
};

export const initialState: ResolverState = { rows: [], divisionAliases: [], loaded: false };

const emptyResolution: RowResolution = {
  action: ROW_ACTION.LEAVE_UNMATCHED,
  library: null,
  newLibraryName: null,
  track: null,
  newTrackTitle: null,
  use: null,
  duration: null,
  duplicateOfLineNumbers: null,
  aliasRemoved: false,
};

const seedRow = (apiRow: ApiResolverRow, partners: ResolverOption[]): ResolverRow => {
  const { status, matchedTrack } = apiRow;
  const vendorFields = {
    itemId: apiRow.itemIds[0],
    itemIds: apiRow.itemIds,
    lineNumbers: apiRow.lineNumbers,
    vendorLibrary: apiRow.trackLibrary,
    vendorTitle: apiRow.trackName,
    use: apiRow.usage,
    duration: apiRow.duration,
  };
  const prefills = {
    use: parseVendorUse(apiRow.usage),
    duration: parseVendorDuration(apiRow.duration),
  };
  const engineMatched = status === 'matched' && matchedTrack != null;
  const resolution = engineMatched
    ? {
        ...emptyResolution,
        ...prefills,
        action: ROW_ACTION.MATCH_EXISTING,
        track: { id: matchedTrack!.id, name: matchedTrack!.title ?? '' },
        library: matchedTrack!.libraryId
          ? { id: matchedTrack!.libraryId, name: matchedTrack!.libraryName ?? '' }
          : null,
      }
    : {
        ...emptyResolution,
        ...prefills,
        library: matchPartnerStrict(apiRow.trackLibrary, partners),
      };
  return { ...vendorFields, engineMatched, resolution };
};

export const isRowMatched = (row: ResolverRow): boolean => {
  const { action, track, newTrackTitle, library, newLibraryName } = row.resolution;
  if (action === ROW_ACTION.MATCH_EXISTING) return track != null;
  if (action === ROW_ACTION.CREATE_NEW) return Boolean(newTrackTitle) && (library != null || Boolean(newLibraryName));
  return false;
};

const updateRow = (
  state: ResolverState,
  itemId: string,
  patch: (resolution: RowResolution) => Partial<RowResolution>,
): ResolverState => ({
  ...state,
  rows: state.rows.map((row) =>
    row.itemId === itemId ? { ...row, resolution: { ...row.resolution, ...patch(row.resolution) } } : row,
  ),
});

const actionForTitle = (newTrackTitle: string | null): RowAction =>
  newTrackTitle ? ROW_ACTION.CREATE_NEW : ROW_ACTION.LEAVE_UNMATCHED;

const normalizeTitle = (title: string): string => (title ?? '').toLowerCase().trim().replace(/\s+/g, ' ');

const findDuplicateCreateNew = (state: ResolverState, itemId: string, title: string): ResolverRow | undefined =>
  state.rows.find(
    (row) =>
      row.itemId !== itemId &&
      row.resolution.newTrackTitle != null &&
      normalizeTitle(row.resolution.newTrackTitle) === normalizeTitle(title),
  );

export const resolverReducer = (state: ResolverState, action: ResolverAction): ResolverState => {
  switch (action.type) {
    case RESOLVER_ACTIONS.ROWS_LOADED:
      return {
        /* Spreadsheet order. The BE already sorts /unmatched by line number; this sort is
           the FE's guarantee of it — lineNumbers is a comma-joined string ("5, 12"), so
           the key is the first number, not the string. */
        rows: [...action.apiRows]
          .sort((a, b) => firstLineNumber(a.lineNumbers) - firstLineNumber(b.lineNumbers))
          .map((apiRow) => seedRow(apiRow, action.partners ?? [])),
        divisionAliases: action.divisionAliases ?? [],
        loaded: true,
      };

    case RESOLVER_ACTIONS.LIBRARY_SELECTED:
      return updateRow(state, action.itemId, (resolution) => ({
        library: action.library,
        newLibraryName: null,
        track: null,
        action: actionForTitle(resolution.newTrackTitle),
        duplicateOfLineNumbers: null,
        aliasRemoved: false,
      }));

    case RESOLVER_ACTIONS.LIBRARY_CREATED:
      return updateRow(state, action.itemId, (resolution) => ({
        newLibraryName: action.name,
        library: null,
        track: null,
        action: actionForTitle(resolution.newTrackTitle),
        duplicateOfLineNumbers: null,
        aliasRemoved: false,
      }));

    case RESOLVER_ACTIONS.TRACK_SELECTED:
      return updateRow(state, action.itemId, () => ({
        track: action.track,
        newTrackTitle: null,
        action: ROW_ACTION.MATCH_EXISTING,
        duplicateOfLineNumbers: null,
        aliasRemoved: false,
      }));

    case RESOLVER_ACTIONS.TRACK_CREATED: {
      const duplicate = findDuplicateCreateNew(state, action.itemId, action.title);
      if (duplicate) {
        return updateRow(state, action.itemId, () => ({
          duplicateOfLineNumbers: duplicate.lineNumbers,
        }));
      }
      return updateRow(state, action.itemId, () => ({
        newTrackTitle: action.title,
        track: null,
        action: ROW_ACTION.CREATE_NEW,
        duplicateOfLineNumbers: null,
        aliasRemoved: false,
      }));
    }

    case RESOLVER_ACTIONS.USE_CHANGED:
      return updateRow(state, action.itemId, () => ({ use: action.use }));

    case RESOLVER_ACTIONS.DURATION_CHANGED:
      return updateRow(state, action.itemId, () => ({ duration: action.duration }));

    case RESOLVER_ACTIONS.ROW_CLEARED:
      return updateRow(state, action.itemId, () => emptyResolution);

    case RESOLVER_ACTIONS.ALIAS_REMOVED:
      return updateRow(state, action.itemId, () => ({ aliasRemoved: true }));

    case RESOLVER_ACTIONS.DIVISION_ALIAS_DELETED: {
      const deletedAlias = state.divisionAliases.find((alias) => alias.id === action.aliasId);
      const divisionAliases = state.divisionAliases.filter((alias) => alias.id !== action.aliasId);
      if (!deletedAlias) return { ...state, divisionAliases };
      return {
        ...state,
        divisionAliases,
        rows: state.rows.map((row) =>
          row.resolution.action === ROW_ACTION.MATCH_EXISTING &&
          row.resolution.track?.id === deletedAlias.qwireTracksId &&
          normalizeAliasTitle(row.vendorTitle) === normalizeAliasTitle(deletedAlias.vendorTitle)
            ? { ...row, resolution: { ...row.resolution, aliasRemoved: true } }
            : row,
        ),
      };
    }

    default:
      return state;
  }
};

export const normalizeAliasTitle = (title: string): string => (title ?? '').trim().toUpperCase();

export type RowAliasView = {
  storedAliases: StoredAlias[];
  pendingVendorTitle: string | null;
  feedback: 'added' | 'removed' | 'conflict' | null;
  conflictTrackTitle?: string;
};

export const deriveRowAliasView = (row: ResolverRow, divisionAliases: StoredAlias[]): RowAliasView => {
  const { action, track, aliasRemoved } = row.resolution;
  if (action !== ROW_ACTION.MATCH_EXISTING || !track) {
    return { storedAliases: [], pendingVendorTitle: null, feedback: null };
  }

  const storedAliases = divisionAliases.filter((alias) => alias.qwireTracksId === track.id);
  const normalizedVendorTitle = normalizeAliasTitle(row.vendorTitle);

  const conflicting = divisionAliases.find(
    (alias) => normalizeAliasTitle(alias.vendorTitle) === normalizedVendorTitle && alias.qwireTracksId !== track.id,
  );
  if (conflicting) {
    return {
      storedAliases,
      pendingVendorTitle: null,
      feedback: 'conflict',
      conflictTrackTitle: conflicting.trackTitle,
    };
  }

  const alreadyStoredOnSameTrack = storedAliases.some(
    (alias) => normalizeAliasTitle(alias.vendorTitle) === normalizedVendorTitle,
  );
  if (alreadyStoredOnSameTrack) {
    return { storedAliases, pendingVendorTitle: null, feedback: null };
  }

  if (aliasRemoved) {
    return { storedAliases, pendingVendorTitle: null, feedback: 'removed' };
  }

  return { storedAliases, pendingVendorTitle: row.vendorTitle, feedback: 'added' };
};

export const isRowFullyResolved = (row: ResolverRow, divisionAliases: StoredAlias[]): boolean =>
  isRowMatched(row) && deriveRowAliasView(row, divisionAliases).feedback !== 'conflict';

export const deriveMatchedCount = (rows: ResolverRow[], divisionAliases: StoredAlias[] = []): number =>
  rows.filter((row) => isRowFullyResolved(row, divisionAliases)).length;

/* Wire-format actions of POST /:jobId/resolve (BE ResolveTrackResolutionDto). The FE's
   kebab-case ROW_ACTION values are internal; the boundary translates. */
const ACTION_TO_API = {
  [ROW_ACTION.MATCH_EXISTING]: 'MATCH_EXISTING',
  [ROW_ACTION.CREATE_NEW]: 'CREATE_NEW',
  [ROW_ACTION.LEAVE_UNMATCHED]: 'LEAVE_UNMATCHED',
} as const;

type ApiResolutionAction = typeof ACTION_TO_API[keyof typeof ACTION_TO_API];

type ResolvePayloadResolution = {
  itemIds: string[];
  action: ApiResolutionAction;
  qwireTracksId?: number | string;
  /* The resolver's "Library" field searches PARTNERS — an existing pick travels as the
     partner's id; a typed name travels as newLibraryName. */
  partnerId?: number | string;
  newLibraryName?: string;
  newTrackName?: string;
  usage?: string;
  duration?: string;
  saveAlias?: boolean;
};

export const buildResolvePayload = (
  rows: ResolverRow[],
  divisionAliases: StoredAlias[] = [],
): { resolutions: ResolvePayloadResolution[] } => ({
  /* Engine-matched rows (Pass A/B/0) are display-only here: the BE rejects resolutions
     for items it matched itself (MATCHED with no resolutionAction), and a single one in
     the batch 400s the whole FINISH IMPORT — hit in the wild the first time a stored
     alias auto-matched a row (QW-2409 Pass 0). Only user-resolvable rows travel. */
  resolutions: rows.filter((row) => !row.engineMatched).map((row) => {
    const { action, track, library, newLibraryName, newTrackTitle, use, duration } = row.resolution;
    /* The resolver's Use/Duration edits are the user's final word for the cue — the BE
       applies them to the items and re-derives cue validity (plan §13 parity). */
    const optional = {
      ...(use != null && { usage: use }),
      ...(duration != null && { duration }),
    };

    if (isRowMatched(row) && action === ROW_ACTION.MATCH_EXISTING && track) {
      const saveAlias = deriveRowAliasView(row, divisionAliases).pendingVendorTitle != null;
      return {
        itemIds: row.itemIds,
        action: ACTION_TO_API[ROW_ACTION.MATCH_EXISTING],
        qwireTracksId: track.id,
        saveAlias,
        ...optional,
      };
    }

    if (isRowMatched(row) && action === ROW_ACTION.CREATE_NEW && newTrackTitle) {
      return {
        itemIds: row.itemIds,
        action: ACTION_TO_API[ROW_ACTION.CREATE_NEW],
        newTrackName: newTrackTitle,
        ...(library ? { partnerId: library.id } : { newLibraryName: newLibraryName as string }),
        ...optional,
      };
    }

    /* Left unmatched on purpose — the BE records the intent (the row's vendor data is
       already on the items, so nothing else travels). */
    return { itemIds: row.itemIds, action: ACTION_TO_API[ROW_ACTION.LEAVE_UNMATCHED], ...optional };
  }),
});
